import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import CentroMapPicker, { geocodificarDireccion } from './CentroMapPicker';
import {
  CalendarDays, Plus, Trash2, Building2, Download,
  Upload, Copy, MapPin, Pencil, X, Globe, Navigation,
} from 'lucide-react';

const TIPO_FESTIVO = { nacional: 'Nacional', autonomico: 'Autonómico', local: 'Local', empresa: 'Empresa' };
const TIPO_STYLE = {
  nacional: 'bg-red-50 text-red-700 border-red-200',
  autonomico: 'bg-amber-50 text-amber-700 border-amber-200',
  local: 'bg-blue-50 text-blue-700 border-blue-200',
  empresa: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};
const CCAA = [
  ['AN', 'Andalucía'], ['AR', 'Aragón'], ['AS', 'Asturias'], ['CN', 'Canarias'],
  ['CB', 'Cantabria'], ['CL', 'Castilla y León'], ['CM', 'Castilla-La Mancha'],
  ['CT', 'Cataluña'], ['EX', 'Extremadura'], ['GA', 'Galicia'], ['IB', 'Islas Baleares'],
  ['RI', 'La Rioja'], ['MD', 'Madrid'], ['MC', 'Murcia'], ['NC', 'Navarra'],
  ['PV', 'País Vasco'], ['VC', 'Comunidad Valenciana'], ['CE', 'Ceuta'], ['ML', 'Melilla'],
];
const CCAA_NAME = Object.fromEntries(CCAA);
const ANIO = new Date().getFullYear();
const ANIOS = [ANIO - 1, ANIO, ANIO + 1, ANIO + 2];

const emptyCentro = { nombre: '', direccion: '', municipio: '', provincia: '', comunidad: '', lat: '', lng: '', radio_m: 100 };

const CalendarioTab = () => {
  const [centros, setCentros] = useState([]);
  const [centroSel, setCentroSel] = useState('');
  const [festivos, setFestivos] = useState([]);
  const [nf, setNf] = useState({ fecha: '', nombre: '', tipo: 'local' });

  // Centros dialog
  // (La gestión completa de centros se ha movido a la pestaña Centros;
  //  aquí sólo listamos los centros para el selector de este calendario.)

  // Import dialogs
  const [apiOpen, setApiOpen] = useState(false);
  const [apiForm, setApiForm] = useState({ anio: ANIO, incluir_nacionales: true, incluir_autonomicos: true });
  const [apiBusy, setApiBusy] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupForm, setDupForm] = useState({ origen_centro_id: '', desplazar_anios: 0 });
  const fileRef = useRef(null);

  const centroActual = centros.find((c) => c.id === centroSel);

  const cargarCentros = () => api.get('/admin/centros').then((r) => {
    setCentros(r.data);
    setCentroSel((prev) => (prev && r.data.some((c) => c.id === prev)) ? prev : (r.data[0]?.id || ''));
  }).catch(() => {});
  const cargarFestivos = (cid) => {
    if (!cid) { setFestivos([]); return; }
    api.get(`/admin/festivos?centro_id=${cid}`).then((r) => setFestivos(r.data)).catch(() => {});
  };

  useEffect(() => { cargarCentros(); }, []);
  useEffect(() => { cargarFestivos(centroSel); }, [centroSel]);

  // ---------- Festivos ----------
  const crearFestivo = async (e) => {
    e.preventDefault();
    if (!centroSel) { toast.error('Selecciona o crea primero un centro de trabajo'); return; }
    try {
      await api.post('/admin/festivos', { ...nf, centro_id: centroSel });
      toast.success('Festivo añadido');
      setNf({ fecha: '', nombre: '', tipo: 'local' });
      cargarFestivos(centroSel); cargarCentros();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const borrarFestivo = async (id) => {
    try { await api.delete(`/admin/festivos/${id}`); cargarFestivos(centroSel); cargarCentros(); }
    catch { toast.error('Error'); }
  };

  // ---------- Import ----------
  const importarApi = async () => {
    setApiBusy(true);
    try {
      const r = await api.post('/admin/festivos/importar-api', { centro_id: centroSel, ...apiForm });
      toast.success(`${r.data.añadidos} festivos oficiales importados`);
      setApiOpen(false); cargarFestivos(centroSel); cargarCentros();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar'); }
    finally { setApiBusy(false); }
  };
  const subirArchivo = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !centroSel) return;
    const fd = new FormData();
    fd.append('centro_id', centroSel);
    fd.append('file', file);
    try {
      const r = await api.post('/admin/festivos/importar-archivo', fd);
      toast.success(`${r.data.añadidos} festivos importados del archivo`);
      cargarFestivos(centroSel); cargarCentros();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo leer el archivo'); }
    finally { if (fileRef.current) fileRef.current.value = ''; }
  };
  const duplicar = async () => {
    if (!dupForm.origen_centro_id) { toast.error('Elige el centro de origen'); return; }
    try {
      const r = await api.post('/admin/festivos/duplicar', {
        origen_centro_id: dupForm.origen_centro_id,
        destino_centro_id: centroSel,
        desplazar_anios: parseInt(dupForm.desplazar_anios || 0, 10),
      });
      toast.success(`${r.data.añadidos} festivos copiados`);
      setDupOpen(false); cargarFestivos(centroSel); cargarCentros();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al duplicar'); }
  };

  // ---------- Ausencias movidas a Empleados › Ausencias ----------

  const sinCentros = centros.length === 0;

  return (
    <div className="space-y-6" data-testid="calendario-tab">
      {/* Barra de centro + acciones */}
      <Card className="border-zinc-200">
        <CardContent className="py-4 flex flex-col lg:flex-row lg:items-end gap-4 flex-wrap">
          <div className="space-y-1.5 min-w-[240px]">
            <Label className="flex items-center gap-1.5"><Building2 className="h-4 w-4" /> Centro de trabajo</Label>
            <Select value={centroSel} onValueChange={setCentroSel} disabled={sinCentros}>
              <SelectTrigger data-testid="centro-selector">
                <SelectValue placeholder={sinCentros ? 'Aún no hay centros' : 'Selecciona centro'} />
              </SelectTrigger>
              <SelectContent>
                {centros.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
            {centroActual && (
              <p className="text-xs text-zinc-500 flex items-center gap-1 pt-0.5">
                <MapPin className="h-3 w-3" />
                {[centroActual.municipio, CCAA_NAME[centroActual.comunidad]].filter(Boolean).join(' · ') || 'Sin ubicación'}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" className="bg-zinc-900 hover:bg-zinc-800" disabled={!centroSel} onClick={() => { setApiForm({ anio: ANIO, incluir_nacionales: true, incluir_autonomicos: true }); setApiOpen(true); }} data-testid="import-api-btn">
              <Globe className="h-4 w-4 mr-1.5" /> Importar oficial
            </Button>
            <Button variant="outline" size="sm" disabled={!centroSel} onClick={() => fileRef.current?.click()} data-testid="import-file-btn">
              <Upload className="h-4 w-4 mr-1.5" /> Subir .ics/CSV
            </Button>
            <input ref={fileRef} type="file" accept=".ics,.csv,text/calendar,text/csv" className="hidden" onChange={subirArchivo} data-testid="import-file-input" />
            <Button variant="outline" size="sm" disabled={!centroSel || centros.length < 2} onClick={() => { setDupForm({ origen_centro_id: '', desplazar_anios: 0 }); setDupOpen(true); }} data-testid="duplicar-btn">
              <Copy className="h-4 w-4 mr-1.5" /> Duplicar de otro
            </Button>
          </div>
        </CardContent>
      </Card>

      {sinCentros && (
        <Card className="border-dashed border-2 border-zinc-200">
          <CardContent className="py-10 text-center space-y-3">
            <Building2 className="h-8 w-8 mx-auto text-zinc-300" />
            <p className="text-zinc-600">Aún no tienes ningún <strong>centro de trabajo</strong>. Créalo primero desde la sección <strong>Centros</strong> para poder gestionar su calendario laboral.</p>
            <p className="text-xs text-zinc-400">Cada centro tiene su propio calendario (nacional + autonómico + local) y su ubicación GPS.</p>
          </CardContent>
        </Card>
      )}

      {!sinCentros && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="border-zinc-200 h-fit">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-lg">
                <CalendarDays className="h-5 w-5" /> Nuevo festivo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={crearFestivo} className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Fecha</Label>
                  <Input type="date" value={nf.fecha} onChange={(e) => setNf({ ...nf, fecha: e.target.value })} required data-testid="new-festivo-fecha" />
                </div>
                <div className="space-y-1.5">
                  <Label>Nombre</Label>
                  <Input value={nf.nombre} onChange={(e) => setNf({ ...nf, nombre: e.target.value })} placeholder="Ej: Fiesta local" required data-testid="new-festivo-nombre" />
                </div>
                <div className="space-y-1.5">
                  <Label>Tipo</Label>
                  <Select value={nf.tipo} onValueChange={(v) => setNf({ ...nf, tipo: v })}>
                    <SelectTrigger data-testid="new-festivo-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TIPO_FESTIVO).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="create-festivo-btn">Añadir festivo</Button>
                <p className="text-xs text-zinc-400 pt-1">Consejo: usa «Importar oficial» para los nacionales y autonómicos, y añade a mano los 2 festivos locales.</p>
              </form>
            </CardContent>
          </Card>

          <Card className="border-zinc-200 lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading text-lg">
                Calendario · {centroActual?.nombre} ({festivos.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-zinc-50 sticky top-0">
                    <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                      <th className="py-3 px-5">Fecha</th><th className="py-3 px-5">Nombre</th><th className="py-3 px-5">Tipo</th><th className="py-3 px-5 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {festivos.map((f) => (
                      <tr key={f.id} className="border-t border-zinc-100" data-testid={`festivo-row-${f.id}`}>
                        <td className="py-2.5 px-5 tabular-nums">{new Date(f.fecha).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}</td>
                        <td className="py-2.5 px-5">{f.nombre}</td>
                        <td className="py-2.5 px-5"><Badge variant="outline" className={TIPO_STYLE[f.tipo]}>{TIPO_FESTIVO[f.tipo] || f.tipo}</Badge></td>
                        <td className="py-2.5 px-5 text-right">
                          <Button size="icon" variant="outline" className="text-red-600" onClick={() => borrarFestivo(f.id)} data-testid={`delete-festivo-${f.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {festivos.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-zinc-400">Sin festivos en este centro. Usa «Importar oficial».</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ausencias movidas a la sección Empleados › Ausencias */}

      {/* Diálogo Importar oficial (API) */}
      <Dialog open={apiOpen} onOpenChange={setApiOpen}>
        <DialogContent data-testid="import-api-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5" /> Importar calendario oficial</DialogTitle>
            <DialogDescription>
              Trae los festivos nacionales y autonómicos de {centroActual?.comunidad ? CCAA_NAME[centroActual.comunidad] : 'la comunidad del centro'} para el año elegido. Los festivos locales se añaden aparte.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Año</Label>
              <Select value={String(apiForm.anio)} onValueChange={(v) => setApiForm({ ...apiForm, anio: parseInt(v, 10) })}>
                <SelectTrigger data-testid="import-api-anio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ANIOS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={apiForm.incluir_nacionales} onChange={(e) => setApiForm({ ...apiForm, incluir_nacionales: e.target.checked })} data-testid="import-nacionales" />
              Festivos nacionales
            </label>
            <label className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={apiForm.incluir_autonomicos} onChange={(e) => setApiForm({ ...apiForm, incluir_autonomicos: e.target.checked })} data-testid="import-autonomicos" />
              Festivos autonómicos {centroActual?.comunidad ? `(${CCAA_NAME[centroActual.comunidad]})` : ''}
            </label>
            {!centroActual?.comunidad && apiForm.incluir_autonomicos && (
              <p className="text-xs text-amber-600">Este centro no tiene Comunidad Autónoma. Edítalo para importar los autonómicos.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApiOpen(false)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" disabled={apiBusy} onClick={importarApi} data-testid="confirm-import-api-btn">
              <Download className="h-4 w-4 mr-1.5" /> {apiBusy ? 'Importando…' : 'Importar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo Duplicar */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent data-testid="duplicar-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="h-5 w-5" /> Duplicar calendario</DialogTitle>
            <DialogDescription>Copia los festivos de otro centro a «{centroActual?.nombre}». No se sobrescriben fechas ya existentes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Centro de origen</Label>
              <Select value={dupForm.origen_centro_id} onValueChange={(v) => setDupForm({ ...dupForm, origen_centro_id: v })}>
                <SelectTrigger data-testid="dup-origen"><SelectValue placeholder="Selecciona centro" /></SelectTrigger>
                <SelectContent>
                  {centros.filter((c) => c.id !== centroSel).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre} ({c.num_festivos})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Desplazar años (para copiar a otro año)</Label>
              <Select value={String(dupForm.desplazar_anios)} onValueChange={(v) => setDupForm({ ...dupForm, desplazar_anios: parseInt(v, 10) })}>
                <SelectTrigger data-testid="dup-desplazar"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Mismas fechas</SelectItem>
                  <SelectItem value="1">+1 año</SelectItem>
                  <SelectItem value="2">+2 años</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={duplicar} data-testid="confirm-dup-btn">
              <Copy className="h-4 w-4 mr-1.5" /> Duplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarioTab;
